import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#app/utils/misc.tsx'
import { Label } from './label.tsx'

/** The aria-wiring props Field clones onto its control child. */
type ControlProps = {
	id?: string
	'aria-invalid'?: React.AriaAttributes['aria-invalid']
	'aria-describedby'?: string
	'aria-required'?: React.AriaAttributes['aria-required']
}

interface FieldProps {
	/** Visible label content. Omit it when the control supplies its own label
	 *  (e.g. a checkbox with an adjacent label): the primitive then renders only
	 *  the stack, the aria wiring, and the message slot — no `<Label>` of its
	 *  own — so it never double-labels the control. */
	label?: React.ReactNode
	/** Id wired to both the Label's `htmlFor` and the control's `id`. */
	htmlFor: string
	/** Optional helper text under the control; linked via `aria-describedby`.
	 *  Shares one message slot with `error` — see `error`. */
	description?: React.ReactNode
	/** When set, the message replaces the description in the same slot (no layout
	 *  shift between the two), the control becomes `aria-invalid`, and it is
	 *  linked to the error via `aria-describedby`. */
	error?: React.ReactNode
	/** Marks the control `aria-required`. Deliberately renders no asterisk — this
	 *  system labels the exceptions with `optional` instead. */
	required?: boolean
	/** Renders a muted "(optional)" suffix after the label. */
	optional?: boolean
	/** The control element (Input, Textarea, Select, Slider, …). */
	children: React.ReactElement<ControlProps>
	className?: string
}

/**
 * Field composite — Label + control + one message slot (description, or the
 * error that replaces it) — that wires `htmlFor`/`id`/`aria-invalid`/
 * `aria-describedby` automatically so consumers stop reinventing error markup.
 * The single child control is cloned to receive the wiring: its `id` is set to
 * `htmlFor`, and when `error` is set it gains `aria-invalid` and an
 * `aria-describedby` pointing at whichever message is shown. Label / control /
 * message stack on the `--space-field` intra-field rhythm; nothing reserves an
 * empty error box when the field is clean. Error text uses `--error-text`; the
 * invalid border comes from each control's own `aria-[invalid]` treatment
 * (`--input-invalid`, the invalid-state contract).
 */
const Field = ({
	label,
	htmlFor,
	description,
	error,
	required,
	optional,
	children,
	className,
}: FieldProps) => {
	const { t } = useTranslation()
	const showError = Boolean(error)
	const messageId = showError
		? `${htmlFor}-error`
		: description
			? `${htmlFor}-description`
			: undefined
	const describedBy =
		[children.props['aria-describedby'], messageId].filter(Boolean).join(' ') ||
		undefined

	const control = React.cloneElement(children, {
		id: htmlFor,
		'aria-invalid': error ? true : children.props['aria-invalid'],
		'aria-describedby': describedBy,
		'aria-required': required || undefined,
	})

	return (
		<div
			data-slot="field"
			className={cn('flex flex-col gap-[var(--space-field)]', className)}
		>
			{label != null ? (
				<Label htmlFor={htmlFor}>
					{label}
					{optional ? (
						<span className="text-muted-foreground font-normal">
							{' '}
							{t('field.optional')}
						</span>
					) : null}
				</Label>
			) : null}
			{control}
			{showError ? (
				<p id={messageId} className="text-error-text text-body-2xs">
					{error}
				</p>
			) : description ? (
				<p id={messageId} className="text-muted-foreground text-body-2xs">
					{description}
				</p>
			) : null}
		</div>
	)
}

export { Field }
export type { ControlProps, FieldProps }
